import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PolicyModal from "../components/PolicyModal";
import axios from "axios";
import "../styles/footer.css";
import {
  fetchActivePolicies,
  findPolicyByTitle,
  PolicyItem,
} from "../utils/policyHelpers";

<link
  href="https://fonts.googleapis.com/css?family=Roboto+Condensed:300,300i,400,400i,700,700i"
  rel="stylesheet"
></link>;

interface FooterItem {
  id: number;
  section: string;
  title: string;
  content: string;
  link: string;
  sort_order: number;
  status: "active" | "inactive";
  createdAt?: string;
  updatedAt?: string;
}

const API_URL =
  import.meta.env.VITE_LYRAZONE_BASE_URL || "http://localhost:5000/api";

const Footer: React.FC = () => {
  const [footerItems, setFooterItems] = useState<FooterItem[]>([]);
  const [policies, setPolicies] = useState<PolicyItem[]>([]);
  const [selectedPolicy, setSelectedPolicy] = useState<PolicyItem | null>(null);

  useEffect(() => {
    fetchFooter();
  }, []);

  useEffect(() => {
    const loadPolicies = async () => {
      try {
        const data = await fetchActivePolicies(API_URL);
        setPolicies(data);
      } catch (error) {
        console.error("Failed to load policies:", error);
      }
    };

    loadPolicies();
  }, [API_URL]);

  const fetchFooter = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/footer/website`);
      const data = Array.isArray(response.data?.data) ? response.data.data : [];
      setFooterItems(
        data.filter((item: FooterItem) => item.status === "active"),
      );
    } catch (error) {
      console.error("Error fetching footer:", error);
      setFooterItems([]);
    }
  };

  const groupedFooter = useMemo(() => {
    return {
      about: footerItems.filter((item) => item.section === "about"),
      useful_links: footerItems.filter(
        (item) => item.section === "useful_links",
      ),
      recent_activities: footerItems.filter(
        (item) => item.section === "recent_activities",
      ),
      contact: footerItems.filter((item) => item.section === "contact"),
      bottom: footerItems.filter((item) => item.section === "bottom"),
    };
  }, [footerItems]);

  const renderFooterLink = (item: FooterItem) => {
    if (!item.link) return <span>{item.title}</span>;

    if (item.link.startsWith("http://") || item.link.startsWith("https://")) {
      return (
        <a href={item.link} target="_blank" rel="noopener noreferrer">
          {item.title}
        </a>
      );
    }

    return <Link to={item.link}>{item.title}</Link>;
  };

  const openPrivacyPolicy = () => {
    const policy = findPolicyByTitle(policies, "Privacy Policy");
    if (policy) setSelectedPolicy(policy);
  };

  const openTermsPolicy = () => {
    const policy =
      findPolicyByTitle(policies, "Terms of Service") ||
      findPolicyByTitle(policies, "Terms of Services");

    if (policy) setSelectedPolicy(policy);
  };

  return (
    <>
      <footer id="footer">
        <div id="footer-s1" className="footer-s1">
          <div className="footer">
            <div className="container">
              <div className="row">
                {/* About Us */}
                <div className="col-md-3 col-sm-6">
                  <div>
                    <img
                      src="/images/logo-w.png"
                      alt="Lyra Zone"
                      className="img-fluid"
                    />
                  </div>
                  <ul className="list-unstyled comp-desc-f">
                    {groupedFooter.about.length > 0 ? (
                      groupedFooter.about.map((item) => (
                        <li key={item.id}>
                          <p>{item.content || item.title}</p>
                        </li>
                      ))
                    ) : (
                      <li>
                        <p>
                          Lyra Zone is a corporate business platform. Our Tech &
                          Services are available internationally. Our solutions
                          will make your business better - catch up with our
                          great ideas.
                        </p>
                      </li>
                    )}
                  </ul>
                </div>

                {/* Useful Links */}
                <div className="col-md-3 col-sm-6">
                  <div className="heading-footer">
                    <h2>Useful Links</h2>
                  </div>
                  <ul className="list-unstyled link-list">
                    {groupedFooter.useful_links.length > 0 ? (
                      groupedFooter.useful_links.map((item) => (
                        <li key={item.id}>
                          {renderFooterLink(item)}{" "}
                          <i className="fa fa-angle-right"></i>
                        </li>
                      ))
                    ) : (
                      <>
                        <li>
                          <Link to="/about">About us</Link>{" "}
                          <i className="fa fa-angle-right"></i>
                        </li>
                        <li>
                          <Link to="/project">Project</Link>{" "}
                          <i className="fa fa-angle-right"></i>
                        </li>
                        <li>
                          <Link to="/careers">Career</Link>{" "}
                          <i className="fa fa-angle-right"></i>
                        </li>
                        <li>
                          <Link to="/contact">Contact us</Link>{" "}
                          <i className="fa fa-angle-right"></i>
                        </li>
                      </>
                    )}
                  </ul>
                </div>

                {/* Recent Activities */}
                <div className="col-md-3 col-sm-6">
                  <div className="heading-footer">
                    <h2>Recent Activities</h2>
                  </div>
                  <ul className="list-unstyled thumb-list">
                    {groupedFooter.recent_activities.length > 0 ? (
                      groupedFooter.recent_activities.map((item) => (
                        <li key={item.id}>
                          <div className="overflow-h">
                            {item.link ? (
                              <a
                                href={item.link}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {item.title}
                              </a>
                            ) : (
                              <span>{item.title}</span>
                            )}
                            <small>
                              {item.content ||
                                (item.createdAt
                                  ? new Date(
                                      item.createdAt,
                                    ).toLocaleDateString()
                                  : "")}
                            </small>
                          </div>
                        </li>
                      ))
                    ) : (
                      <>
                        <li>
                          <div className="overflow-h">
                            <a
                              href="http://elbs.com.my"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Our Joint Venture Partner
                            </a>
                            <small>15 March, 2018</small>
                          </div>
                        </li>
                        <li>
                          <div className="overflow-h">
                            <a
                              href="http://fict.lyrazone.com.my"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Launching of FICT
                            </a>
                            <small>21 December, 2024</small>
                          </div>
                        </li>
                      </>
                    )}
                  </ul>
                </div>

                {/* Get In Touch */}
                <div className="col-md-3 col-sm-6">
                  <div className="heading-footer">
                    <h2>Get In Touch</h2>
                  </div>

                  {groupedFooter.contact.length > 0 ? (
                    <>
                      <address className="address-details-f">
                        {groupedFooter.contact.map((item) => (
                          <div key={item.id}>
                            {item.title && <strong>{item.title}</strong>}
                            <br />
                            <span style={{ whiteSpace: "pre-line" }}>
                              {item.content}
                            </span>
                            {item.link && (
                              <>
                                <br />
                                <a
                                  href={
                                    item.link.startsWith("mailto:") ||
                                    item.link.startsWith("tel:")
                                      ? item.link
                                      : `mailto:${item.link}`
                                  }
                                >
                                  {item.link.replace("mailto:", "")}
                                </a>
                              </>
                            )}
                            <br />
                            <br />
                          </div>
                        ))}
                      </address>
                    </>
                  ) : (
                    <>
                      <address className="address-details-f">
                        Suite 4.06, 4th Floor,
                        <br />
                        Menara KH, Jalan Sultan Ismail,
                        <br />
                        50250 Kuala Lumpur, Malaysia.
                        <br />
                        Phone: +60 3 2691 7067
                        <br />
                        Email:{" "}
                        <a href="mailto:support@lyrazone.com.my">
                          support@lyrazone.com.my
                        </a>
                      </address>
                    </>
                  )}

                  <ul className="list-inline social-icon-f top-data">
                    <li>
                      <a
                        href="https://www.youtube.com/channel/UC4HuaQMMFnFcLRB3IirK-Sg"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <i className="fa top-social fa-youtube"></i>
                      </a>
                    </li>
                    <li>
                      <a
                        href="https://www.facebook.com/profile.php?id=61553204526797&mibextid=kFxxJD"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <i className="fa top-social fa-facebook"></i>
                      </a>
                    </li>
                    <li>
                      <a
                        href="https://www.linkedin.com/company/lyra-zone-solutions/posts/?feedView=all"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <i className="fa top-social fa-linkedin"></i>
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Bottom */}
        <div id="footer-bottom">
          <div className="container">
            <div className="row">
              <div className="col-md-12">
                <div id="footer-copyrights">
                  {groupedFooter.bottom.length > 0 ? (
                    <p>
                      {groupedFooter.bottom.map((item, index) => {
                        const label = (item.title || "").trim().toLowerCase();
                        const isPrivacyPolicy = label === "privacy policy";
                        const isTermsPolicy =
                          label === "terms of service" ||
                          label === "terms of services";
                        const isPolicyItem = isPrivacyPolicy || isTermsPolicy;
                        const isLink = !!item.link && !isPolicyItem;

                        return (
                          <React.Fragment key={item.id}>
                            {" "}
                            {index > 0 && <span> | </span>}{" "}
                            {isPolicyItem ? (
                              <span
                                className="footer-policy-link"
                                onClick={
                                  isPrivacyPolicy
                                    ? openPrivacyPolicy
                                    : openTermsPolicy
                                }
                                style={{ cursor: "pointer" }}
                              >
                                {item.title}
                              </span>
                            ) : isLink ? (
                              item.link.startsWith("/") ? (
                                <Link to={item.link}>{item.title}</Link>
                              ) : (
                                <a
                                  href={item.link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {item.title}
                                </a>
                              )
                            ) : (
                              <span>{item.content || item.title}</span>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </p>
                  ) : (
                    <p>
                      Copyright © 2025 All Rights Reserved by Lyra Zone Sdn.
                      Bhd.{" "}
                      <span
                        className="footer-policy-link"
                        onClick={openPrivacyPolicy}
                      >
                        Privacy Policy
                      </span>{" "}
                      |{" "}
                      <span
                        className="footer-policy-link"
                        onClick={openTermsPolicy}
                      >
                        Terms of Services
                      </span>
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <a
          href="#"
          id="back-to-top"
          className="btn btn-sm btn-silver btn-back-to-top smooth-scrolls"
          title="home"
          style={{ borderRadius: "50%" }}
        >
          <i
            className="fa fa-angle-up"
            style={{ padding: "8px", fontSize: "25px" }}
          ></i>
        </a>
      </footer>
      <PolicyModal
        isOpen={!!selectedPolicy}
        title={selectedPolicy?.title || ""}
        body={selectedPolicy?.body || ""}
        onClose={() => setSelectedPolicy(null)}
      />
    </>
  );
};

export default Footer;
